import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { FiUserPlus, FiShield, FiRefreshCw, FiCheck, FiSlash, FiEdit2, FiKey } from 'react-icons/fi';
import toast from 'react-hot-toast';
import { authApi, tokenizersApi, legalReviewFirmsApi } from '../../api/smartTokenizerApi';
import { useTokenIssuer } from '../../contexts/TokenIssuerContext';
import { getStoredSession } from '../../api/authStorage';

const ASSIGNABLE_ROLES = [
  { value: 'Admin', label: 'Admin' },
  { value: 'LegalReviewer', label: 'Legal reviewer' },
  { value: 'AuthorizedTokenizer', label: 'Authorized tokenizer' },
  { value: 'Investor', label: 'Investor' },
  { value: 'Auditor', label: 'Auditor' },
];

const USER_STATUS = {
  Active: 1,
  Suspended: 2,
  PendingApproval: 3,
};

/** API may return enum as number or string (JsonStringEnumConverter). */
function normalizeStatus(raw) {
  if (raw === USER_STATUS.Active || raw === 'Active' || raw === 'active') return USER_STATUS.Active;
  if (raw === USER_STATUS.Suspended || raw === 'Suspended' || raw === 'suspended') return USER_STATUS.Suspended;
  if (raw === USER_STATUS.PendingApproval || raw === 'PendingApproval' || raw === 'pendingApproval') {
    return USER_STATUS.PendingApproval;
  }
  return raw;
}

function statusLabel(status) {
  const s = normalizeStatus(status);
  switch (s) {
    case USER_STATUS.Active:
      return 'Active';
    case USER_STATUS.Suspended:
      return 'Suspended';
    case USER_STATUS.PendingApproval:
      return 'Pending approval';
    default:
      return String(status);
  }
}

/** AuthorizedTokenizerStatus / LegalReviewFirmStatus: Pending=1, Approved=2, Suspended=3 */
function registryEntityState(record) {
  const s = record?.status ?? record?.Status;
  if (s === null || s === undefined) return 'unknown';
  const lower = String(s).toLowerCase();
  if (lower === 'approved' || s === 2) return 'approved';
  if (lower === 'suspended' || s === 3) return 'suspended';
  if (lower === 'pending' || s === 1) return 'pending';
  return 'unknown';
}

function isApprovedTokenizerRecord(t) {
  return registryEntityState(t) === 'approved';
}

function isApprovedFirmRecord(f) {
  return registryEntityState(f) === 'approved';
}

function tokenizerOptionLabel(t) {
  const name = t.organizationName ?? t.OrganizationName ?? 'Tokenizer';
  const reg = t.registrationNumber ?? t.RegistrationNumber ?? '';
  const base = reg ? `${name} (${reg})` : name;
  const st = registryEntityState(t);
  if (st === 'approved') return base;
  if (st === 'pending') return `${base} — approve tokenizer first`;
  if (st === 'suspended') return `${base} — suspended`;
  return `${base} — not assignable`;
}

function firmOptionLabel(f) {
  const name = f.organizationName ?? f.OrganizationName ?? 'Firm';
  const reg = f.registrationNumber ?? f.RegistrationNumber ?? '';
  const base = reg ? `${name} (${reg})` : name;
  const st = registryEntityState(f);
  if (st === 'approved') return base;
  if (st === 'pending') return `${base} — approve firm first`;
  if (st === 'suspended') return `${base} — suspended`;
  return `${base} — not assignable`;
}

function assignmentLabel(u, tokenizers, firms) {
  const at = u.authorizedTokenizerId ?? u.AuthorizedTokenizerId;
  const lf = u.legalReviewFirmId ?? u.LegalReviewFirmId;
  const roles = u.roles ?? u.Roles ?? [];
  const r = roles.map((x) => String(x).toLowerCase());
  if (r.includes('authorizedtokenizer') && at) {
    const t = tokenizers.find((x) => String(x.id ?? x.Id) === String(at));
    return t ? t.organizationName ?? t.OrganizationName ?? '—' : String(at).slice(0, 8) + '…';
  }
  if (r.includes('legalreviewer') && lf) {
    const f = firms.find((x) => String(x.id ?? x.Id) === String(lf));
    return f ? f.organizationName ?? f.OrganizationName ?? '—' : String(lf).slice(0, 8) + '…';
  }
  return '—';
}

const Page = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
  min-height: calc(100vh - 80px);
`;

const Title = styled.h1`
  font-size: 1.75rem;
  margin-bottom: 0.5rem;
  color: #fff;
  display: flex;
  align-items: center;
  gap: 0.75rem;

  svg {
    color: #4facfe;
  }
`;

const Lead = styled.p`
  color: #a0a0a0;
  margin-bottom: 1.5rem;
  line-height: 1.6;
`;

const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  align-items: flex-end;
  margin-bottom: 1.5rem;
`;

const CardTitle = styled.h2`
  font-size: 1.1rem;
  color: #4facfe;
  margin-bottom: 1rem;
`;

const Field = styled.div`
  margin-bottom: 1rem;

  label {
    display: block;
    color: #e0e0e0;
    font-size: 0.85rem;
    margin-bottom: 0.35rem;
  }

  input,
  select {
    width: 100%;
    max-width: 400px;
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.15);
    border-radius: 8px;
    padding: 0.65rem 0.85rem;
    color: #fff;
    font-size: 0.95rem;

    &:focus {
      outline: none;
      border-color: #4facfe;
    }
  }
`;

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  align-items: flex-end;
`;

const Submit = styled.button`
  padding: 0.65rem 1.25rem;
  background: linear-gradient(135deg, #4facfe, #00f2fe);
  color: #0f0f23;
  border: none;
  border-radius: 8px;
  font-weight: 700;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const IconButton = styled.button`
  padding: 0.4rem 0.65rem;
  flex-shrink: 0;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 6px;
  color: #e0e0e0;
  cursor: pointer;
  font-size: 0.8rem;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;

  &:hover:not(:disabled) {
    background: rgba(79, 172, 254, 0.2);
    border-color: #4facfe;
    color: #fff;
  }

  &:disabled {
    opacity: 0.35;
    cursor: not-allowed;
  }
`;

/** Icon-only row actions — keeps the users table within layout width without horizontal scroll. */
const TableIconAction = styled.button`
  flex-shrink: 0;
  width: 2.125rem;
  height: 2.125rem;
  padding: 0;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 6px;
  color: #e8e8e8;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;

  &:hover:not(:disabled) {
    background: rgba(79, 172, 254, 0.2);
    border-color: #4facfe;
    color: #fff;
  }

  &:disabled {
    opacity: 0.35;
    cursor: not-allowed;
  }
`;

const TableWrap = styled.div`
  width: 100%;
  max-width: 100%;
  overflow-x: hidden;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
`;

const Table = styled.table`
  width: 100%;
  table-layout: fixed;
  border-collapse: collapse;
  font-size: 0.9rem;

  th,
  td {
    padding: 0.65rem 0.7rem;
    text-align: left;
    vertical-align: middle;
    border-bottom: 1px solid rgba(255, 255, 255, 0.06);
    min-width: 0;
    overflow-wrap: anywhere;
    word-break: break-word;
  }

  th {
    color: #a0a0a0;
    font-weight: 600;
    background: rgba(0, 0, 0, 0.2);
  }

  td {
    color: #e8e8e8;
  }

  tr:last-child td {
    border-bottom: none;
  }

  th:last-child,
  td:last-child {
    overflow-wrap: normal;
    word-break: normal;
  }
`;

const ActionsGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.3rem;
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 0.2rem 0.55rem;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 600;
  background: ${(p) =>
    p.$status === USER_STATUS.Active
      ? 'rgba(34, 197, 94, 0.2)'
      : p.$status === USER_STATUS.Suspended
        ? 'rgba(248, 113, 113, 0.2)'
        : 'rgba(251, 191, 36, 0.2)'};
  color: ${(p) =>
    p.$status === USER_STATUS.Active ? '#86efac' : p.$status === USER_STATUS.Suspended ? '#fecaca' : '#fde68a'};
`;

const Denied = styled.div`
  background: rgba(255, 100, 100, 0.08);
  border: 1px solid rgba(255, 100, 100, 0.25);
  border-radius: 12px;
  padding: 1.5rem;
  color: #fecaca;
  line-height: 1.6;
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.75);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 11000;
  padding: 1rem;
`;

const Modal = styled.div`
  background: #1a1a3e;
  border: 1px solid rgba(79, 172, 254, 0.3);
  border-radius: 12px;
  padding: 1.5rem;
  width: 100%;
  max-width: 440px;
`;

const ModalTitle = styled.h3`
  margin-bottom: 1rem;
  color: #fff;
`;

function AdminUserManagement() {
  const { isAdmin } = useTokenIssuer();
  const hasSession = Boolean(getStoredSession()?.accessToken);
  const currentUserId = getStoredSession()?.userId ? String(getStoredSession().userId) : null;

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [organizationName, setOrganizationName] = useState('');
  const [role, setRole] = useState('Investor');
  const [createAuthorizedTokenizerId, setCreateAuthorizedTokenizerId] = useState('');
  const [createLegalReviewFirmId, setCreateLegalReviewFirmId] = useState('');

  const [registryTokenizers, setRegistryTokenizers] = useState([]);
  const [registryFirms, setRegistryFirms] = useState([]);

  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [editFullName, setEditFullName] = useState('');
  const [editOrg, setEditOrg] = useState('');
  const [editRole, setEditRole] = useState('Investor');
  const [editAuthorizedTokenizerId, setEditAuthorizedTokenizerId] = useState('');
  const [editLegalReviewFirmId, setEditLegalReviewFirmId] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);

  const [setPasswordOpen, setSetPasswordOpen] = useState(false);
  const [setPasswordUser, setSetPasswordUser] = useState(null);
  const [tempPw, setTempPw] = useState('');
  const [tempPwConfirm, setTempPwConfirm] = useState('');
  const [savingTempPw, setSavingTempPw] = useState(false);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await authApi.listUsers();
      setUsers(Array.isArray(data) ? data : []);
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Failed to load users';
      toast.error(msg);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadRegistry = useCallback(async () => {
    try {
      const [tRes, fRes] = await Promise.all([tokenizersApi.list(), legalReviewFirmsApi.list()]);
      setRegistryTokenizers(Array.isArray(tRes.data) ? tRes.data : []);
      setRegistryFirms(Array.isArray(fRes.data) ? fRes.data : []);
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Failed to load tokenizer / firm registry';
      toast.error(msg);
      setRegistryTokenizers([]);
      setRegistryFirms([]);
    }
  }, []);

  useEffect(() => {
    if (hasSession && isAdmin) {
      loadUsers();
      loadRegistry();
    }
  }, [hasSession, isAdmin, loadUsers, loadRegistry]);

  const resetCreateForm = () => {
    setEmail('');
    setPassword('');
    setFullName('');
    setOrganizationName('');
    setRole('Investor');
    setCreateAuthorizedTokenizerId('');
    setCreateLegalReviewFirmId('');
  };

  const openCreate = () => {
    resetCreateForm();
    setCreateOpen(true);
  };

  const closeCreate = () => {
    setCreateOpen(false);
    resetCreateForm();
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (role === 'AuthorizedTokenizer' && !createAuthorizedTokenizerId) {
      toast.error('Choose an approved authorized tokenizer for this operator account.');
      return;
    }
    if (role === 'LegalReviewer' && !createLegalReviewFirmId) {
      toast.error('Choose an approved legal reviewer firm for this user.');
      return;
    }
    setCreating(true);
    try {
      const body = {
        email: email.trim(),
        password,
        fullName: fullName.trim(),
        organizationName: organizationName.trim(),
        role
      };
      if (role === 'AuthorizedTokenizer') {
        body.authorizedTokenizerId = createAuthorizedTokenizerId;
      }
      if (role === 'LegalReviewer') {
        body.legalReviewFirmId = createLegalReviewFirmId;
      }
      await authApi.registerUser(body);
      toast.success('User created (pending approval until you activate them).');
      closeCreate();
      await loadUsers();
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Failed to create user';
      toast.error(msg);
    } finally {
      setCreating(false);
    }
  };

  const openEdit = (u) => {
    setEditUser(u);
    setEditFullName(u.fullName ?? u.FullName ?? '');
    setEditOrg(u.organizationName ?? u.OrganizationName ?? '');
    const roles = u.roles ?? u.Roles ?? [];
    const r = roles.length > 0 ? roles[0] : 'Investor';
    setEditRole(ASSIGNABLE_ROLES.some((x) => x.value === r) ? r : 'Investor');
    const at = u.authorizedTokenizerId ?? u.AuthorizedTokenizerId;
    const lf = u.legalReviewFirmId ?? u.LegalReviewFirmId;
    setEditAuthorizedTokenizerId(at ? String(at) : '');
    setEditLegalReviewFirmId(lf ? String(lf) : '');
    setEditOpen(true);
  };

  const closeEdit = () => {
    setEditOpen(false);
    setEditUser(null);
  };

  const openSetTemporaryPassword = (u) => {
    setSetPasswordUser(u);
    setTempPw('');
    setTempPwConfirm('');
    setSetPasswordOpen(true);
  };

  const closeSetTemporaryPassword = () => {
    setSetPasswordOpen(false);
    setSetPasswordUser(null);
    setTempPw('');
    setTempPwConfirm('');
  };

  const handleSetTemporaryPassword = async (e) => {
    e?.preventDefault();
    if (!setPasswordUser) return;
    const pw = tempPw;
    if (!pw.trim()) {
      toast.error('Enter a new temporary password.');
      return;
    }
    if (pw !== tempPwConfirm) {
      toast.error('Password and confirmation do not match.');
      return;
    }
    setSavingTempPw(true);
    try {
      const uid = setPasswordUser.userId ?? setPasswordUser.UserId;
      await authApi.setUserPassword(uid, { newPassword: pw });
      toast.success('Temporary password set. Share it securely with the user.');
      closeSetTemporaryPassword();
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Failed to set password';
      toast.error(msg);
    } finally {
      setSavingTempPw(false);
    }
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    if (!editUser) return;
    if (editRole === 'AuthorizedTokenizer' && !editAuthorizedTokenizerId) {
      toast.error('Select an approved authorized tokenizer for this role.');
      return;
    }
    if (editRole === 'LegalReviewer' && !editLegalReviewFirmId) {
      toast.error('Select an approved legal reviewer firm for this role.');
      return;
    }
    setSavingEdit(true);
    try {
      const payload = {
        fullName: editFullName.trim(),
        organizationName: editOrg.trim(),
        role: editRole,
        applyAuthorizedTokenizerAssignment: editRole === 'AuthorizedTokenizer',
        authorizedTokenizerId: editRole === 'AuthorizedTokenizer' ? editAuthorizedTokenizerId : null,
        applyLegalReviewFirmAssignment: editRole === 'LegalReviewer',
        legalReviewFirmId: editRole === 'LegalReviewer' ? editLegalReviewFirmId : null
      };
      await authApi.updateUser(editUser.userId ?? editUser.UserId, payload);
      toast.success('User updated');
      closeEdit();
      await loadUsers();
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Update failed';
      toast.error(msg);
    } finally {
      setSavingEdit(false);
    }
  };

  const handleApprove = async (userId) => {
    try {
      await authApi.approveUser(userId);
      toast.success('User activated');
      await loadUsers();
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Failed';
      toast.error(msg);
    }
  };

  const handleSuspend = async (userId) => {
    try {
      await authApi.suspendUser(userId);
      toast.success('User suspended');
      await loadUsers();
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Failed';
      toast.error(msg);
    }
  };

  if (!hasSession) {
    return (
      <Page>
        <Title>
          <FiUserPlus />
          User management
        </Title>
        <Denied>Sign in with the Login button in the header to access this page.</Denied>
      </Page>
    );
  }

  if (!isAdmin) {
    return (
      <Page>
        <Title>
          <FiShield />
          User management
        </Title>
        <Denied>
          Your account does not have the Admin role. Only administrators can manage users.
        </Denied>
      </Page>
    );
  }

  return (
    <Page>
      <Title>
        <FiUserPlus />
        User management
      </Title>
      <Lead>
        View all platform users. New users you create start as <strong>pending approval</strong> until you activate
        them. <strong>Authorized tokenizer</strong> and <strong>Legal reviewer</strong> accounts must be linked to an
        approved registry entry (tokenizers and legal reviewer firms). Use <strong>Set password</strong> for a temporary
        password. Users can change their own password from <strong>My account</strong>.
      </Lead>

      <Toolbar>
        <Submit
          type="button"
          onClick={() => {
            loadUsers();
            loadRegistry();
          }}
          disabled={loading}
        >
          <FiRefreshCw />
          {loading ? 'Refreshing…' : 'Refresh list'}
        </Submit>
        <Submit type="button" onClick={openCreate} disabled={loading}>
          <FiUserPlus />
          Create user
        </Submit>
      </Toolbar>

      <CardTitle style={{ marginBottom: '0.75rem', color: '#fff' }}>Existing users</CardTitle>
      <TableWrap>
        <Table>
          <colgroup>
            <col style={{ width: '24%' }} />
            <col style={{ width: '11%' }} />
            <col style={{ width: '10%' }} />
            <col style={{ width: '12%' }} />
            <col style={{ width: '18%' }} />
            <col style={{ width: '9%' }} />
            <col style={{ width: '16%' }} />
          </colgroup>
          <thead>
            <tr>
              <th>Email</th>
              <th>Name</th>
              <th>Organization</th>
              <th>Roles</th>
              <th>Registry assignment</th>
              <th>Status</th>
              <th scope="col">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.length === 0 && !loading && (
              <tr>
                <td colSpan={7} style={{ color: '#888' }}>
                  No users loaded.
                </td>
              </tr>
            )}
            {users.map((u) => {
              const uid = u.userId ?? u.UserId;
              const idStr = String(uid);
              const isSelf = currentUserId && idStr === currentUserId;
              const st = normalizeStatus(u.status ?? u.Status);
              const canActivate = st !== USER_STATUS.Active;
              const canSuspend = st !== USER_STATUS.Suspended && !isSelf;
              const email = u.email ?? u.Email;
              const fn = u.fullName ?? u.FullName;
              const org = u.organizationName ?? u.OrganizationName;
              const roles = u.roles ?? u.Roles ?? [];
              return (
                <tr key={idStr}>
                  <td>{email}</td>
                  <td>{fn}</td>
                  <td>{org}</td>
                  <td>{roles.join(', ') || '—'}</td>
                  <td>{assignmentLabel(u, registryTokenizers, registryFirms)}</td>
                  <td>
                    <StatusBadge $status={st}>{statusLabel(st)}</StatusBadge>
                  </td>
                  <td>
                    <ActionsGroup>
                      <TableIconAction
                        type="button"
                        title="Edit user"
                        aria-label="Edit user"
                        onClick={() => openEdit(u)}
                        disabled={loading}
                      >
                        <FiEdit2 aria-hidden />
                      </TableIconAction>
                      <TableIconAction
                        type="button"
                        title="Set temporary password"
                        aria-label="Set temporary password"
                        onClick={() => openSetTemporaryPassword(u)}
                        disabled={loading}
                      >
                        <FiKey aria-hidden />
                      </TableIconAction>
                      <TableIconAction
                        type="button"
                        title="Activate user"
                        aria-label="Activate user"
                        onClick={() => handleApprove(uid)}
                        disabled={loading || !canActivate}
                      >
                        <FiCheck aria-hidden />
                      </TableIconAction>
                      <TableIconAction
                        type="button"
                        title="Suspend user"
                        aria-label="Suspend user"
                        onClick={() => handleSuspend(uid)}
                        disabled={loading || !canSuspend}
                      >
                        <FiSlash aria-hidden />
                      </TableIconAction>
                    </ActionsGroup>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </TableWrap>

      {createOpen && (
        <ModalOverlay onClick={(e) => e.target === e.currentTarget && !creating && closeCreate()}>
          <Modal
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: '520px' }}
          >
            <ModalTitle>Create user</ModalTitle>
            <form onSubmit={handleCreate}>
              <Row>
                <Field>
                  <label>Email</label>
                  <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
                </Field>
                <Field>
                  <label>Temporary password</label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={8}
                    autoComplete="new-password"
                  />
                </Field>
              </Row>
              <p style={{ color: '#888', fontSize: '0.8rem', marginBottom: '0.5rem', maxWidth: '480px' }}>
                Use at least 8 characters with uppercase, lowercase, and a digit (same rules as password change).
              </p>
              <Row>
                <Field>
                  <label>Full name</label>
                  <input type="text" value={fullName} onChange={(e) => setFullName(e.target.value)} required />
                </Field>
                <Field>
                  <label>Organization</label>
                  <input
                    type="text"
                    value={organizationName}
                    onChange={(e) => setOrganizationName(e.target.value)}
                    required
                  />
                </Field>
                <Field>
                  <label>Role</label>
                  <select
                    value={role}
                    onChange={(e) => {
                      setRole(e.target.value);
                      setCreateAuthorizedTokenizerId('');
                      setCreateLegalReviewFirmId('');
                    }}
                  >
                    {ASSIGNABLE_ROLES.map((r) => (
                      <option key={r.value} value={r.value}>
                        {r.label}
                      </option>
                    ))}
                  </select>
                </Field>
              </Row>
              {role === 'AuthorizedTokenizer' && (
                <Field>
                  <label>Authorized tokenizer</label>
                  <select
                    value={createAuthorizedTokenizerId}
                    onChange={(e) => setCreateAuthorizedTokenizerId(e.target.value)}
                    required
                  >
                    <option value="">Select tokenizer…</option>
                    {[...registryTokenizers]
                      .sort((a, b) => (isApprovedTokenizerRecord(a) ? 0 : 1) - (isApprovedTokenizerRecord(b) ? 0 : 1))
                      .map((t) => {
                        const id = t.id ?? t.Id;
                        const ok = isApprovedTokenizerRecord(t);
                        return (
                          <option key={String(id)} value={String(id)} disabled={!ok}>
                            {tokenizerOptionLabel(t)}
                          </option>
                        );
                      })}
                  </select>
                  {registryTokenizers.length > 0 && !registryTokenizers.some(isApprovedTokenizerRecord) && (
                    <p style={{ color: '#fbbf24', fontSize: '0.82rem', marginTop: '0.4rem', lineHeight: 1.45 }}>
                      No approved tokenizers in the list. Open <strong>Authorized tokenizers</strong>, approve the
                      registry entry, then use Refresh list here.
                    </p>
                  )}
                </Field>
              )}
              {role === 'LegalReviewer' && (
                <Field>
                  <label>Legal reviewer firm</label>
                  <select
                    value={createLegalReviewFirmId}
                    onChange={(e) => setCreateLegalReviewFirmId(e.target.value)}
                    required
                  >
                    <option value="">Select firm…</option>
                    {[...registryFirms]
                      .sort((a, b) => (isApprovedFirmRecord(a) ? 0 : 1) - (isApprovedFirmRecord(b) ? 0 : 1))
                      .map((f) => {
                        const id = f.id ?? f.Id;
                        const ok = isApprovedFirmRecord(f);
                        return (
                          <option key={String(id)} value={String(id)} disabled={!ok}>
                            {firmOptionLabel(f)}
                          </option>
                        );
                      })}
                  </select>
                  {registryFirms.length > 0 && !registryFirms.some(isApprovedFirmRecord) && (
                    <p style={{ color: '#fbbf24', fontSize: '0.82rem', marginTop: '0.4rem', lineHeight: 1.45 }}>
                      Firms you add start as <strong>pending</strong> until approved. Open{' '}
                      <strong>Legal reviewer firms</strong>, click <strong>Approve</strong>, then use Refresh list on
                      this page.
                    </p>
                  )}
                  {registryFirms.length === 0 && (
                    <p style={{ color: '#a0a0a0', fontSize: '0.82rem', marginTop: '0.4rem', lineHeight: 1.45 }}>
                      No firms loaded. Register one under <strong>Legal reviewer firms</strong>, then refresh.
                    </p>
                  )}
                </Field>
              )}
              <Row style={{ marginTop: '1rem' }}>
                <Submit type="submit" disabled={creating}>
                  {creating ? 'Creating…' : 'Create user'}
                </Submit>
                <IconButton type="button" onClick={closeCreate} disabled={creating}>
                  Cancel
                </IconButton>
              </Row>
            </form>
          </Modal>
        </ModalOverlay>
      )}

      {setPasswordOpen && setPasswordUser && (
        <ModalOverlay
          onClick={(e) => e.target === e.currentTarget && !savingTempPw && closeSetTemporaryPassword()}
        >
          <Modal onClick={(e) => e.stopPropagation()} style={{ maxWidth: '440px' }}>
            <ModalTitle>Set temporary password</ModalTitle>
            <p style={{ color: '#a0a0a0', fontSize: '0.9rem', marginBottom: '1rem' }}>
              {setPasswordUser.email ?? setPasswordUser.Email ?? ''}
            </p>
            <form onSubmit={handleSetTemporaryPassword}>
              <Field>
                <label>New temporary password</label>
                <input
                  type="password"
                  value={tempPw}
                  onChange={(e) => setTempPw(e.target.value)}
                  minLength={8}
                  autoComplete="new-password"
                  required
                />
              </Field>
              <Field>
                <label>Confirm temporary password</label>
                <input
                  type="password"
                  value={tempPwConfirm}
                  onChange={(e) => setTempPwConfirm(e.target.value)}
                  minLength={8}
                  autoComplete="new-password"
                  required
                />
              </Field>
              <p style={{ color: '#888', fontSize: '0.8rem', marginBottom: '0.75rem' }}>
                8+ characters, uppercase, lowercase, and a digit (platform policy).
              </p>
              <Row style={{ marginTop: '1rem' }}>
                <Submit type="submit" disabled={savingTempPw}>
                  {savingTempPw ? 'Setting…' : 'Set temporary password'}
                </Submit>
                <IconButton type="button" onClick={closeSetTemporaryPassword} disabled={savingTempPw}>
                  Cancel
                </IconButton>
              </Row>
            </form>
          </Modal>
        </ModalOverlay>
      )}

      {editOpen && editUser && (
        <ModalOverlay onClick={(e) => e.target === e.currentTarget && !savingEdit && closeEdit()}>
          <Modal onClick={(e) => e.stopPropagation()} style={{ maxWidth: '480px' }}>
            <ModalTitle>Edit user</ModalTitle>
            <form onSubmit={saveEdit}>
              <Field>
                <label>Email</label>
                <input type="text" value={editUser.email ?? editUser.Email ?? ''} disabled />
              </Field>
              <Field>
                <label>Full name</label>
                <input type="text" value={editFullName} onChange={(e) => setEditFullName(e.target.value)} required />
              </Field>
              <Field>
                <label>Organization</label>
                <input type="text" value={editOrg} onChange={(e) => setEditOrg(e.target.value)} required />
              </Field>
              <Field>
                <label>Role</label>
                <select
                  value={editRole}
                  onChange={(e) => {
                    setEditRole(e.target.value);
                    setEditAuthorizedTokenizerId('');
                    setEditLegalReviewFirmId('');
                  }}
                >
                  {ASSIGNABLE_ROLES.map((r) => (
                    <option key={r.value} value={r.value}>
                      {r.label}
                    </option>
                  ))}
                </select>
              </Field>
              {editRole === 'AuthorizedTokenizer' && (
                <Field>
                  <label>Authorized tokenizer</label>
                  <select
                    value={editAuthorizedTokenizerId}
                    onChange={(e) => setEditAuthorizedTokenizerId(e.target.value)}
                    required
                  >
                    <option value="">Select tokenizer…</option>
                    {[...registryTokenizers]
                      .sort((a, b) => (isApprovedTokenizerRecord(a) ? 0 : 1) - (isApprovedTokenizerRecord(b) ? 0 : 1))
                      .map((t) => {
                        const id = t.id ?? t.Id;
                        const ok = isApprovedTokenizerRecord(t);
                        return (
                          <option key={String(id)} value={String(id)} disabled={!ok}>
                            {tokenizerOptionLabel(t)}
                          </option>
                        );
                      })}
                  </select>
                  {registryTokenizers.length > 0 && !registryTokenizers.some(isApprovedTokenizerRecord) && (
                    <p style={{ color: '#fbbf24', fontSize: '0.82rem', marginTop: '0.4rem', lineHeight: 1.45 }}>
                      Approve a tokenizer under <strong>Authorized tokenizers</strong>, then refresh the user list.
                    </p>
                  )}
                </Field>
              )}
              {editRole === 'LegalReviewer' && (
                <Field>
                  <label>Legal reviewer firm</label>
                  <select
                    value={editLegalReviewFirmId}
                    onChange={(e) => setEditLegalReviewFirmId(e.target.value)}
                    required
                  >
                    <option value="">Select firm…</option>
                    {[...registryFirms]
                      .sort((a, b) => (isApprovedFirmRecord(a) ? 0 : 1) - (isApprovedFirmRecord(b) ? 0 : 1))
                      .map((f) => {
                        const id = f.id ?? f.Id;
                        const ok = isApprovedFirmRecord(f);
                        const currentId = editLegalReviewFirmId ? String(editLegalReviewFirmId) : '';
                        const isCurrent = currentId && String(id) === currentId;
                        return (
                          <option
                            key={String(id)}
                            value={String(id)}
                            disabled={!ok && !isCurrent}
                          >
                            {firmOptionLabel(f)}
                          </option>
                        );
                      })}
                  </select>
                  {registryFirms.length > 0 && !registryFirms.some(isApprovedFirmRecord) && (
                    <p style={{ color: '#fbbf24', fontSize: '0.82rem', marginTop: '0.4rem', lineHeight: 1.45 }}>
                      Approve the firm under <strong>Legal reviewer firms</strong>, then refresh. Pending firms appear
                      greyed out until then.
                    </p>
                  )}
                  {registryFirms.length === 0 && (
                    <p style={{ color: '#a0a0a0', fontSize: '0.82rem', marginTop: '0.4rem', lineHeight: 1.45 }}>
                      No firms loaded — refresh or register under <strong>Legal reviewer firms</strong>.
                    </p>
                  )}
                </Field>
              )}
              <Row style={{ marginTop: '1rem' }}>
                <Submit type="submit" disabled={savingEdit}>
                  {savingEdit ? 'Saving…' : 'Save'}
                </Submit>
                <IconButton type="button" onClick={closeEdit} disabled={savingEdit}>
                  Cancel
                </IconButton>
              </Row>
            </form>
          </Modal>
        </ModalOverlay>
      )}
    </Page>
  );
}

export default AdminUserManagement;
